import { Autocomplete, Collapse, FormControlLabel, Radio, RadioGroup, TextField } from "@mui/material";
import React from "react";
import { useIntl } from "react-intl";

import { type InputProps, OTHER } from "../answers";
import { useReadOnly } from "../readOnly";

import { fieldSx, optionSx } from "./optionSx";
import { OtherField } from "./OtherField";

export const SEARCHABLE_FROM = 12;

export const SingleSelectInput: React.FC<InputProps<"singleSelect">> = ({
    question,
    answer,
    onChange,
    error,
    compact,
}) => {
    const readOnly = useReadOnly();
    const { formatMessage } = useIntl();
    if (compact || question.options.length > SEARCHABLE_FROM) {
        const choices = [
            ...question.options,
            ...(question.allowOther ? [{ id: OTHER, label: formatMessage({ defaultMessage: "Other" }) }] : []),
        ];
        const value = typeof answer?.value === "string" && answer.value ? answer.value : null;
        return (
            <>
                <Autocomplete
                    disabled={readOnly}
                    size="small"
                    autoHighlight
                    options={choices.map((c) => c.id)}
                    getOptionLabel={(id) => choices.find((c) => c.id === id)?.label ?? id}
                    value={value}
                    onChange={(_, id) => onChange({ ...answer, value: id ?? undefined })}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            error={error}
                            placeholder={formatMessage({ defaultMessage: "Search or select" })}
                        />
                    )}
                    sx={fieldSx}
                />
                <Collapse in={value === OTHER} unmountOnExit>
                    <OtherField answer={answer} onChange={onChange} error={error} />
                </Collapse>
            </>
        );
    }
    return (
        <RadioGroup
            sx={{ gap: 0.5 }}
            value={answer?.value ?? ""}
            onChange={(event) => onChange({ ...answer, value: event.target.value })}
        >
            {question.options.map((option) => (
                <FormControlLabel
                    sx={optionSx}
                    key={option.id}
                    value={option.id}
                    control={<Radio />}
                    label={option.label}
                />
            ))}
            {question.allowOther && (
                <FormControlLabel
                    sx={optionSx}
                    value={OTHER}
                    control={<Radio />}
                    label={formatMessage({ defaultMessage: "Other" })}
                />
            )}
            <Collapse in={answer?.value === OTHER} unmountOnExit>
                <OtherField answer={answer} onChange={onChange} error={error} />
            </Collapse>
        </RadioGroup>
    );
};
